// crewbit-admin/src/components/layout/sidebarMoneySubItems.ts
//
// SPEC §Money.NavigationLayerRealization Phase B / D9-D17. Sub-item config
// for the expandable Money sidebar entry. The 8 entries here mirror the
// 8 /money/* sub-routes and are universal across all clusters in v1
// (per-cluster gating queued as §Money.SidebarSubItemClusterGating).
//
// Badge sourcing (per D14): Outstanding/Disputes/Tax read live from
// useFinancialSummary; Invoices/Expenses/Recurring/Revenue/Settings render
// without badges in v1 (no per-route count surface in the envelope yet).

import {
  AlertCircle,
  Clock,
  FileText,
  Percent,
  Receipt,
  RefreshCw,
  Settings as SettingsIcon,
  TrendingUp,
} from 'lucide-react';
import type { FinancialSummary } from '../../hooks/useFinancialSummary';
import { palette } from '../../styles/tokens';

export type MoneySubItemId =
  | 'money-outstanding'
  | 'money-invoices'
  | 'money-expenses'
  | 'money-recurring'
  | 'money-revenue'
  | 'money-tax'
  | 'money-disputes'
  | 'money-settings';

// Cluster keys mirror sidebarConfig.ts CLUSTER_MAP (resolved from
// useIndustryLens().clusterKey). NOTE: the C3 spec named this SidebarItemId[],
// but clusters are keyed 'A'–'G', not top-level nav items — corrected here so
// the field is coherent against the live cluster model.
export type MoneyClusterKey = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

export interface MoneySubItem {
  id: MoneySubItemId;
  label: string;
  path: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  // C3 P2 — per-cluster gating. Absent = universal (shown for every cluster).
  clusters?: MoneyClusterKey[];
}

export const MONEY_SUB_ITEMS: MoneySubItem[] = [
  { id: 'money-outstanding', label: 'Outstanding', path: '/money/outstanding', icon: Clock },
  { id: 'money-invoices', label: 'Invoices', path: '/money/invoices', icon: FileText },
  { id: 'money-expenses', label: 'Expenses', path: '/money/expenses', icon: Receipt },
  { id: 'money-recurring', label: 'Recurring', path: '/money/recurring', icon: RefreshCw },
  { id: 'money-revenue', label: 'Revenue', path: '/money/revenue', icon: TrendingUp },
  { id: 'money-tax', label: 'Tax', path: '/money/tax', icon: Percent },
  { id: 'money-disputes', label: 'Disputes', path: '/money/disputes', icon: AlertCircle },
  { id: 'money-settings', label: 'Settings', path: '/money/settings', icon: SettingsIcon },
];

// C3 P2 — per-cluster gating mechanism for the Money sub-nav (clears
// §Money.SidebarSubItemClusterGating). Mirrors sidebarConfig.ts
// CLUSTER_LABEL_OVERRIDES. Seedless in P2 (mechanism only) → resolveMoneySubItems
// is an identity over MONEY_SUB_ITEMS, so there is no visual change yet; P3
// populates the conservative seed + threads the active cluster into the
// SidebarMoneyNavItem render that consumes this resolver.
export const MONEY_SUB_ITEM_CLUSTER_LABEL_OVERRIDES: Record<
  string,
  Partial<Record<MoneySubItemId, string>>
> = {};

// Resolve the visible, relabeled Money sub-items for the active cluster:
//   - filter by per-item `clusters` (absent = universal),
//   - apply the per-cluster label override when present.
// `cluster` is the clusterKey ('A'–'G') from useIndustryLens(); null/unknown
// falls back to the universal set (items without a `clusters` restriction).
export function resolveMoneySubItems(cluster: string | null | undefined): MoneySubItem[] {
  return MONEY_SUB_ITEMS.filter(
    (item) =>
      !item.clusters ||
      (cluster != null && item.clusters.includes(cluster as MoneyClusterKey)),
  ).map((item) => {
    const override =
      cluster != null ? MONEY_SUB_ITEM_CLUSTER_LABEL_OVERRIDES[cluster]?.[item.id] : undefined;
    return override ? { ...item, label: override } : item;
  });
}

export interface MoneySubItemBadge {
  value: string;
  color: string;
}

// D14 — badge derivation per sub-item. Returns null when no badge applies.
export function getMoneySubItemBadge(
  id: MoneySubItemId,
  summary: FinancialSummary | null,
): MoneySubItemBadge | null {
  if (!summary) return null;

  switch (id) {
    case 'money-outstanding': {
      const count = summary.outstanding.invoice_count;
      if (count <= 0) return null;
      const hasOverdue =
        summary.outstanding.buckets['31_60'].count > 0 ||
        summary.outstanding.buckets['61_plus'].count > 0;
      return {
        value: String(count),
        color: hasOverdue ? palette.amber : palette.accent,
      };
    }
    case 'money-disputes': {
      const count = summary.disputes_open.count;
      if (count <= 0) return null;
      return { value: String(count), color: palette.amber };
    }
    case 'money-tax': {
      const days = summary.tax_summary.days_until_due;
      if (days === null || days > 30 || summary.tax_summary.status !== 'open') return null;
      return {
        value: `${days}d`,
        color: days <= 7 ? palette.amber : palette.accent,
      };
    }
    default:
      return null;
  }
}
